/**
 * Khao Ddi design tokens. The brand colour is the logo background, #98C4DF, used
 * as is for headers, buttons and selected states. Every other blue is the same
 * hue (203°) made lighter for backgrounds or darker for text; gold and red are
 * the logo's tagline and seal. Pure data so the Next.js console can import them
 * without pulling in react-native.
 */
export const colors = {
  /** Logo background: headers. */
  sky: '#98C4DF',
  /** Logo blue at a depth that holds white text (4.9:1): solid chips, banners, admin sidebar. */
  navy: '#38779F',
  /** Main brand colour, the logo background. Fills only, with onPrimary on top. */
  primary: '#98C4DF',
  primaryDark: '#82B7D9',
  /** Text, icons and selection outlines in the brand colour on light backgrounds (6.7:1 on white). */
  primaryText: '#2A6184',
  /** Text and icons on a primary fill (5.5:1); white is too faint on the logo blue. */
  onPrimary: '#1C455F',
  /** Loyalty gold. */
  gold: '#FFDC6E',
  goldDeep: '#E0B238',
  goldText: '#6B5310',
  goldSoft: '#FDF6DD',
  red: '#E4362F',
  redText: '#9E2A22',
  redSoft: '#FDE8E7',
  danger: '#B3261E',
  green: '#2F7D5B',
  greenText: '#1F5A40',
  greenSoft: '#EAF5EF',
  mint: '#7BD3A6',

  background: '#F4F8FB',
  surface: '#FFFFFF',
  surfaceMuted: '#E8F1F7',
  border: '#D5E6F1',
  borderStrong: '#C2D8E5',
  divider: '#EDF3F7',
  inputBorder: '#B9CDDA',
  mapBg: '#E3EEF5',

  text: '#1C455F',
  textMuted: '#486170',
  textSubtle: '#8597A3',
  textOnDark: '#FFFFFF',
  textOnDarkMuted: '#F4F8FB',
  textOnDarkSoft: '#FFFFFF',
} as const;

/**
 * Logo blue fading lighter, for large brand surfaces (member card, shipper
 * headers). Use dark text on it: colors.text is at least 5.5:1 on every stop.
 */
export const BRAND_GRADIENT = 'linear-gradient(160deg, #98C4DF 0%, #C9DFED 100%)';

export const fonts = {
  display: "'Baloo 2', system-ui, sans-serif",
  body: "'Be Vietnam Pro', system-ui, sans-serif",
} as const;

export const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700;800&family=Be+Vietnam+Pro:wght@400;500;600;700&display=swap';

export const radii = { xs: 7, sm: 10, md: 14, lg: 18, xl: 22, pill: 999 } as const;

export const spacing = { xxs: 4, xs: 6, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24 } as const;

/** Minimum touch target from the design (44 px). */
export const HIT = 44;

/** Status chip colours shared by the apps and console. */
export const tones = {
  positive: { bg: colors.greenSoft, fg: colors.greenText },
  warning: { bg: colors.goldSoft, fg: colors.goldText },
  danger: { bg: colors.redSoft, fg: colors.redText },
  info: { bg: colors.surfaceMuted, fg: colors.primaryDark },
  neutral: { bg: '#EEF1F3', fg: '#5A6B78' },
} as const;

export type Tone = keyof typeof tones;

/** Shared with the console CSS: phones below `tablet`, the full side nav from `desktop`. */
export const BREAKPOINTS = { tablet: 768, desktop: 1024 } as const;

/** Side nav widths in the RN-web apps: icon rail on tablets, labelled column on desktop. */
export const SIDE_NAV_WIDTH = { tablet: 72, desktop: 240 } as const;

/** Wide screens centre the content at this width instead of stretching it. */
export const CONTENT_MAX_WIDTH = 1200;
